import React, { useState, useRef, useEffect } from 'react';
import { ChevronLeft, ChevronRight, ChevronDown } from 'lucide-react';

interface PromotionPaginationProps {
    currentPage: number;
    totalItems: number;
    pageSize: number;
    onPageSizeChange: (newPageSize: number) => void;
    loading?: boolean;
    loadingLinked?: boolean; // Novo: indica se está carregando produtos vinculados
    onNextPage: () => void;
    onPreviousPage: () => void;
    hasNextPage: boolean;
    hasPreviousPage: boolean;
}

const PAGE_SIZE_OPTIONS = [25, 50, 75, 100, 150, 200, 300, 400, 500];

export const PromotionPagination: React.FC<PromotionPaginationProps> = ({
    currentPage,
    totalItems,
    pageSize,
    onPageSizeChange,
    loading = false,
    loadingLinked = false,
    onNextPage,
    onPreviousPage,
    hasNextPage,
    hasPreviousPage
}) => {
    const [showPageSizeDropdown, setShowPageSizeDropdown] = useState(false);
    const dropdownRef = useRef<HTMLDivElement>(null);

    // Fechar dropdown ao clicar fora
    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (dropdownRef.current && !dropdownRef.current.contains(event.target as Node)) {
                setShowPageSizeDropdown(false);
            }
        };

        if (showPageSizeDropdown) {
            document.addEventListener('mousedown', handleClickOutside);
        }

        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [showPageSizeDropdown]);

    const handlePageSizeSelect = (newPageSize: number) => {
        if (newPageSize !== pageSize) {
            onPageSizeChange(newPageSize);
        }
        setShowPageSizeDropdown(false);
    };

    if (totalItems === 0 && !hasNextPage && !hasPreviousPage && !loadingLinked) {
        return null;
    }

    return (
        <div className="bg-white p-4 rounded-lg shadow-sm mt-6">
            <div className="flex flex-col sm:flex-row items-center justify-between gap-4">
                {/* Items info */}
                <div className="text-sm text-gray-600 flex items-center gap-2">
                    {totalItems > 0 || loadingLinked ? (
                        <>
                            <span>Página <span className="font-semibold">{currentPage}</span> -</span>
                            <div className="relative" ref={dropdownRef}>
                                <button
                                    onClick={() => setShowPageSizeDropdown(!showPageSizeDropdown)}
                                    disabled={loading || loadingLinked}
                                    className="font-semibold hover:text-blue-600 transition-colors flex items-center gap-1 disabled:opacity-50 disabled:cursor-not-allowed"
                                    title="Alterar tamanho da página"
                                >
                                    <span>{pageSize}</span>
                                    <ChevronDown 
                                        size={16} 
                                        className={`transition-transform ${showPageSizeDropdown ? 'rotate-180' : ''}`}
                                    />
                                </button>
                                
                                {showPageSizeDropdown && (
                                    <div className="absolute top-full left-0 mt-1 bg-white border border-gray-200 rounded-lg shadow-lg z-50 min-w-[80px]">
                                        {PAGE_SIZE_OPTIONS.map((size) => (
                                            <button
                                                key={size}
                                                onClick={() => handlePageSizeSelect(size)}
                                                className={`w-full px-3 py-2 text-left hover:bg-gray-100 transition-colors ${
                                                    size === pageSize 
                                                        ? 'bg-blue-50 text-blue-600 font-semibold' 
                                                        : 'text-gray-700'
                                                } first:rounded-t-lg last:rounded-b-lg`}
                                            >
                                                {size}
                                            </button>
                                        ))}
                                    </div>
                                )}
                            </div>
                            <span>produtos</span>
                        </>
                    ) : (
                        <span>Nenhum produto encontrado</span>
                    )}
                </div>

                {/* Pagination controls - apenas setas anterior/próximo */}
                <div className="flex items-center gap-2">
                    {/* Previous page */}
                    <button
                        onClick={onPreviousPage}
                        disabled={!hasPreviousPage || loading || loadingLinked}
                        className={`p-2 rounded-lg border transition-colors ${
                            !hasPreviousPage || loading || loadingLinked
                                ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                : 'hover:bg-gray-100 border-gray-300'
                        }`}
                        title="Página anterior"
                    >
                        <ChevronLeft size={20} />
                    </button>

                    {/* Next page */}
                    <button
                        onClick={onNextPage}
                        disabled={!hasNextPage || loading || loadingLinked}
                        className={`p-2 rounded-lg border transition-colors ${
                            !hasNextPage || loading || loadingLinked
                                ? 'opacity-50 cursor-not-allowed bg-gray-100'
                                : 'hover:bg-gray-100 border-gray-300'
                        }`}
                        title="Próxima página"
                    >
                        <ChevronRight size={20} />
                    </button>
                </div>
            </div>
        </div>
    );
};

